/**
 * Тема оформления.
 *
 * Три состояния, а не два: «системная» — это не то же самое, что
 * «светлая» или «тёмная». Человек, выбравший тёмный терминал в светлой
 * системе, не должен получить переключение под руками, когда система
 * сменит тему по расписанию.
 *
 * Тема применяется атрибутом на корне документа: CSS переопределяет
 * токены, а не отдельные правила. Компоненты о теме не знают вовсе —
 * иначе каждый новый экран пришлось бы учить обеим.
 *
 * График живёт вне CSS: у него своя палитра, которую надо переключать
 * вызовом. Поэтому здесь же лежит подписка — тот, кто рисует, узнаёт о
 * смене темы и перекрашивается.
 */

import type { ThemeSetting } from '@specter/types';
import { applyTheme, type ThemeName } from '../chart/theme.js';
import { paintTelegramChrome, telegramTheme } from './telegram-app.js';

type Listener = (theme: ThemeName) => void;

const listeners = new Set<Listener>();
let current: ThemeName = 'dark';

/**
 * Где помнится выбор до прихода профиля.
 *
 * Профиль лежит на сервере и приходит через сотни миллисекунд после
 * первого кадра. Всё это время человек со светлой темой смотрел бы на
 * тёмный экран, который потом моргает, — поэтому выбор дублируется в
 * браузере и применяется до отрисовки.
 */
const STORAGE_KEY = 'specter:theme';

/** Какая тема сейчас на экране. */
export function activeTheme(): ThemeName {
  return current;
}

/** Что выбрала бы система, если человек не выбрал сам. */
function systemTheme(): ThemeName {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Разрешить настройку профиля в конкретную тему.
 *
 * Внутри Telegram «как в системе» означает «как в Telegram», а не «как
 * в телефоне»: мини-приложение живёт внутри чужого окна, и светлый
 * терминал в тёмном мессенджере выглядит не выбором человека, а
 * недоделкой. Свой выбор при этом сильнее — он остаётся выбором.
 */
export function resolveTheme(setting: ThemeSetting): ThemeName {
  if (setting !== 'system') return setting;
  return telegramTheme() ?? systemTheme();
}

/**
 * Применить тему к документу и графику.
 *
 * Вызывается при загрузке профиля и при каждой смене настройки.
 * Повторный вызов с той же темой ничего не делает: перерисовывать
 * график впустую незачем.
 */
export function useThemeSetting(setting: ThemeSetting): void {
  const theme = resolveTheme(setting);
  if (theme === current && document.documentElement.dataset['theme']) return;

  current = theme;
  document.documentElement.dataset['theme'] = theme;
  try {
    localStorage.setItem(STORAGE_KEY, setting);
  } catch {
    // Приватный режим или запрет хранилища — тема просто не переживёт
    // перезагрузку. Ронять из-за этого терминал незачем.
  }
  applyTheme(theme);
  // Шапка окна Telegram красится вместе со страницей: полоса чужого
  // цвета над терминалом выдаёт стык двух приложений.
  paintTelegramChrome();
  for (const listener of listeners) listener(theme);
}

/** Подписка на смену темы: для графика, который живёт вне CSS. */
export function onThemeChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Следить за системной темой, пока человек не выбрал свою.
 *
 * Возвращает функцию отписки. Пересоздаётся при смене настройки: когда
 * выбор сделан, слушать систему больше не нужно.
 */
export function watchSystemTheme(setting: ThemeSetting): () => void {
  if (setting !== 'system' || typeof window === 'undefined' || !window.matchMedia) {
    return () => undefined;
  }

  const media = window.matchMedia('(prefers-color-scheme: light)');
  const onChange = (): void => useThemeSetting('system');
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

/**
 * Применить запомненную тему до первого кадра.
 *
 * Вызывается из точки входа, до отрисовки: атрибут на корне должен
 * стоять раньше, чем браузер посчитает первые стили. Иначе человек
 * видит вспышку чужой темы — короткую, но заметную и неприятную.
 */
export function applyStoredTheme(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    stored = null;
  }

  const setting: ThemeSetting =
    // Ничего не запомнено — новый человек: ему тёмный терминал, как и в
    // профиле по умолчанию. Иначе первый кадр был бы светлым и моргал.
    stored === 'dark' || stored === 'light' || stored === 'system' ? stored : 'dark';

  current = resolveTheme(setting);
  document.documentElement.dataset['theme'] = current;
  applyTheme(current);
}
